import type { ProductAnalyticsPublicDto } from './product-analytics-storage'
import type { AnalyticsReturns } from './returns-analytics'

export type CurrentTurnoverSource = 'productAnalytics' | 'ymarketFinancialReport' | 'unavailable'
export type CurrentTurnoverAvailability = 'available' | 'partial' | 'unavailable'

type CurrentTurnoverMoney = {
  unit: 'kopecks'
  currency: 'RUB'
  grossRevenueKopecks: number
  netRevenueKopecks: number
}

export type CurrentMonthTurnoverSelection =
  | (CurrentTurnoverMoney & {
      month: string
      source: 'productAnalytics'
      availability: 'available'
      throughDay: number
      incomeKopecks: number | null
      reason: 'operational_current_month'
    })
  | (CurrentTurnoverMoney & {
      month: string
      source: 'ymarketFinancialReport'
      availability: 'available' | 'partial'
      returnTransactions: number
      coveredFrom?: string
      coveredThrough?: string
      reason: 'financial_report_current_sale_cohort'
    })
  | {
      month: string
      source: 'unavailable'
      availability: 'unavailable'
      unit: 'kopecks'
      currency: 'RUB'
      reason: 'current_sale_month_not_proven'
    }

/** The only human-facing clock used to select the current sale cohort. */
export function europeRigaMonth(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Riga', year: 'numeric', month: '2-digit',
  }).formatToParts(now)
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value
  return `${part('year')}-${part('month')}`
}

/**
 * Select one current sale cohort. Booking-month refunds deliberately do not
 * participate here: they are exposed separately as currentBookedReturns.
 */
export function selectCurrentMonthTurnover(
  products: ProductAnalyticsPublicDto | null,
  returns: AnalyticsReturns,
  now = new Date(),
): CurrentMonthTurnoverSelection {
  const month = europeRigaMonth(now)
  const operational = products?.monthToDate
  if (operational?.month === month) {
    return {
      month,
      source: 'productAnalytics',
      availability: 'available',
      unit: 'kopecks',
      currency: 'RUB',
      grossRevenueKopecks: operational.turnoverKopecks,
      netRevenueKopecks: operational.turnoverKopecks,
      incomeKopecks: operational.incomeKopecks,
      throughDay: operational.throughDay,
      reason: 'operational_current_month',
    }
  }
  const financial = returns.monthly.find((point) => point.month === month)
  if (financial && financial.availability !== 'unavailable') {
    return {
      month,
      source: 'ymarketFinancialReport',
      availability: financial.availability,
      unit: 'kopecks',
      currency: 'RUB',
      grossRevenueKopecks: Math.round(financial.grossRevenue * 100),
      netRevenueKopecks: Math.round(financial.netRevenue * 100),
      returnTransactions: financial.returnTransactions,
      ...(financial.availability === 'partial'
        ? { coveredFrom: financial.coveredFrom, coveredThrough: financial.coveredThrough }
        : {}),
      reason: 'financial_report_current_sale_cohort',
    }
  }
  return {
    month,
    source: 'unavailable',
    availability: 'unavailable',
    unit: 'kopecks',
    currency: 'RUB',
    reason: 'current_sale_month_not_proven',
  }
}
